import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/features/auth/AuthContext';
import { listMembers } from '@/lib/api';
import { listCards } from '@/lib/cards';
import { listAccounts, listTransactions } from '@/lib/finance';
import { listRecurrences } from '@/lib/recurrences';

const DISMISS_KEY = 'cssystem.getting-started.dismissed';

interface Step {
  label: string;
  hint: string;
  to: string;
  done: boolean;
}

/** Checklist de primeiros passos no dashboard — some quando tudo estiver feito ou for dispensado. */
export function GettingStarted() {
  const { me } = useAuth();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: listAccounts, enabled: !dismissed });
  const txQ = useQuery({
    queryKey: ['transactions', {}],
    queryFn: () => listTransactions({}),
    enabled: !dismissed,
  });
  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: listCards, enabled: !dismissed });
  const recsQ = useQuery({ queryKey: ['recurrences'], queryFn: listRecurrences, enabled: !dismissed });
  const householdId = me?.household?.id ?? '';
  const membersQ = useQuery({
    queryKey: ['members', householdId],
    queryFn: () => listMembers(householdId),
    enabled: !dismissed && householdId !== '',
  });

  if (dismissed) return null;
  const loaded = accountsQ.data && txQ.data && cardsQ.data && recsQ.data;
  if (!loaded) return null;

  const steps: Step[] = [
    {
      label: 'Crie sua primeira conta',
      hint: 'corrente, poupança ou a carteira de dinheiro vivo',
      to: '/accounts',
      done: (accountsQ.data?.length ?? 0) > 0,
    },
    {
      label: 'Registre um lançamento',
      hint: 'uma despesa ou receita qualquer, pra pegar o jeito',
      to: '/transactions?new=1',
      done: (txQ.data?.total ?? 0) > 0,
    },
    {
      label: 'Cadastre seu cartão de crédito',
      hint: 'com dia de fechamento e vencimento',
      to: '/cards',
      done: (cardsQ.data?.length ?? 0) > 0,
    },
    {
      label: 'Crie uma recorrência',
      hint: 'aluguel, assinatura, salário — o app lança sozinho',
      to: '/recurrences',
      done: (recsQ.data?.length ?? 0) > 0,
    },
    {
      label: 'Convide alguém da casa',
      hint: 'compartilhem as finanças no mesmo espaço',
      to: '/help#compartilhado',
      done: (membersQ.data?.length ?? 1) > 1,
    },
  ];

  const remaining = steps.filter((s) => !s.done).length;
  if (remaining === 0) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          🚀 comece por aqui ({steps.length - remaining}/{steps.length})
        </h2>
        <button
          type="button"
          onClick={dismiss}
          className="tap text-xs text-slate-400 hover:text-slate-600 hover:underline"
        >
          dispensar
        </button>
      </div>
      <ul className="flex flex-col gap-1 text-sm">
        {steps.map((s) => (
          <li key={s.label}>
            {s.done ? (
              <span className="flex items-center gap-2 px-2 py-1.5 text-slate-400">
                <span aria-hidden>✅</span>
                <span className="line-through">{s.label}</span>
              </span>
            ) : (
              <Link
                to={s.to}
                className="tap flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50"
              >
                <span aria-hidden>⬜</span>
                <span className="font-medium text-slate-900">{s.label}</span>
                <span className="hidden text-slate-400 sm:inline">— {s.hint}</span>
                <span className="ml-auto text-slate-400">→</span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
